import { Grid } from '../../../../components/common/Grid';
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Chip, Stack, ToggleButtonGroup, ToggleButton, useTheme, alpha, CircularProgress, Dialog, DialogTitle, DialogContent, IconButton, Divider, Accordion, AccordionSummary, AccordionDetails, Alert, Tabs, Tab, TextField, InputAdornment, TableSortLabel } from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  LocalPharmacy as PharmacyIcon,
  AccountBalance as InsuranceIcon,
  AcUnit as AcUnitIcon,
  Search as SearchIcon,
  CameraAlt as CameraAltIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import api from '../../../../services/api';
import { usePatientAllPrescriptions } from '../../../../hooks/queries/usePatientPrescriptions';
import { parsePatientId } from '../../../../hooks/queries/usePatientBasicInfo';
import { useBlockHidden } from '../../../../hooks/queries/usePharmacyVisibility';
import { usePatientOrders } from '../../../../hooks/queries/usePatientOrders';
import { usePatient360ReadOnly } from '../context/Patient360ReadOnlyContext';
import Checkbox from '@mui/material/Checkbox';
import { RxActiveStatusCell, DispenseStatusCell } from './prescriptions/RxStatusCell';
import { usePersistedTableColumns, type TableColumnDef } from '../../../../hooks/usePersistedTableColumns';
import { TableColumnChooser } from '../../../../components/common/TableColumnChooser';
import { rxOrderStagesByRx, rxOrderStageLabel } from './prescriptions/rxOrderStage';
import { MarkSpecialtyDialog, NO_DRUG_NAME_REASON, type MarkSpecialtyRx } from './prescriptions/MarkSpecialtyDialog';
import { usePharmacyStore } from '../../../../stores/pharmacyStore';
import { HardcopyViewer } from '../../../../components/common/HardcopyViewer';
import type { HardcopySignature } from '../../../../components/common/HardcopyViewer';
import RxImagePanel, { RxImagePanelMedication } from '../../../../components/common/RxImagePanel';
import { RxFillHistoryTable } from '../../../../components/common/RxFillHistoryDialog';
import { PatientNotesPanel } from '../../../../components/patient-360/core/PatientNotesPanel';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import DescriptionIcon from '@mui/icons-material/Description';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import { tintSuccessCard, panelNeutral, stickyHeaderSurface } from '../../../../theme/gridSurfaces';
import RxCounselingCard, { toHardcopyCounselingEntries } from '../../../../components/patient-360/RxCounselingCard';
import CounselingDialog from '../../../../components/counseling/CounselingDialog';
import { usePatientCounselingRecords, filterCounselingForRx, filterCounselingForRxNumbers, type CounselingRecord } from '../../../../hooks/queries/usePatientCounseling';
import { deriveCounselFlagsFromCounseling, pickCounselingRecordForRx } from '../../../../constants/counselingConstants';
import { useBatchUserLookup } from '../../../../hooks/useUserLookup';
import { RxAdjStateBadge } from '../../../../components/common/RxStatusBadge';
import { usePatientVerificationPhotos } from '../../../../hooks/useVerificationPhotos';
import { VerificationPhotoLightbox } from '../../../../components/product-verification/VerificationPhotoLightbox';
import type { PatientVerificationPhoto } from '../../../../services/verificationPhotoService';
import { usePatientRecord } from '../../../../hooks/queries/usePatientRecord';
import { apiErrorMessage } from '../../../../utils/apiErrorMessage';
// PRX-137: the Prescriptions tab extras live in their own components.
import { HideDeletedToggle, useHideDeletedPreference, withoutDeleted } from './prescriptions/HideDeletedToggle';
import { RxStatusLegend } from './prescriptions/RxStatusLegend';
import { RxInteractionsChip } from './prescriptions/RxInteractionsChip';
import { RxRenalDosingChip } from './prescriptions/RxRenalDosingChip';
import { RxRowActionsMenu } from './prescriptions/RxRowActionsMenu';
import { VacationSupplyView } from './prescriptions/vacation/VacationSupplyView';
import { RxInfoSheetDialog } from './prescriptions/RxInfoSheetDialog';
import { DueDaysCell, InteractionFlag, PrnCell, SpecialtyBadge, isByRequestOnly, isSpecialtyRx, specialtyDrugNames } from './prescriptions/RxCells';
import { buildRxRowActions, byRequestOnlyPlan, type ByRequestOnlyPlan, type RxRowActionKey, type RxRowActionsInput } from './prescriptions/rxRowActions';
import { ByRequestOnlyConfirmDialog } from './prescriptions/ByRequestOnlyConfirmDialog';
import { useRxBasketLines, useSetRxByRequestOnly } from './prescriptions/useRxBasketLines';

/**
 * Rx Detail dialog tabs. Counseling sits beside the Hardcopy (GP-1358): the
 * counseling for a prescription used to live only under Notes & Documents →
 * RX History, so answering "was this Rx counselled?" meant leaving the
 * prescription you were looking at.
 */
const TAB_HARDCOPY = 0;
const TAB_COUNSELING = 1;
const TAB_FILL_HISTORY = 2;
const TAB_INSURANCE = 3;
const TAB_DETAILS = 4;
const TAB_NOTES = 5;

interface AllPrescriptionsProps {
  patientId: string;
  /**
   * Opt in to the "Mark as specialty" selection (checkbox column, toolbar and
   * dialog). Patient 360 passes it; the Clinical Verification mount
   * (`UnifiedPatientInfoDialog`) does not — it sits outside the Patient 360
   * read-only provider, and the server would refuse the writes anyway.
   *
   * `recordPharmacyId` is the pharmacy id Patient 360's record read is keyed
   * on (`compositePharmacyId`), handed down rather than re-derived here: the
   * marks are posted to it and the record read is refreshed under it. It
   * resolves to the same pharmacy as the table's own `pharmacyId` below, but
   * the page owns the key its record read is cached under, so it says which
   * one it is rather than leaving this table to derive it a second time.
   */
  specialtySelection?: { recordPharmacyId: string };
  /**
   * PRX-137: the Patient 360 row actions, PRN column, specialty badge and
   * interaction check. Opt-in like `specialtySelection`; the Clinical
   * Verification mount passes neither. `recordPharmacyId` keys the basket and
   * record reads the same way the page keys them; `canEditBilling` is the
   * page's one `editBilling` answer; `onOpenAuditTrail` is absent when the
   * Audit log tab is not available.
   */
  rxActions?: {
    recordPharmacyId: string;
    canEditBilling: boolean;
    onOpenAuditTrail?: (rxNumber: string) => void;
  };
  /**
   * PRX-204: told the ticked Rx numbers (the ones the filter shows) whenever
   * they change, and an empty list on unmount, so Patient 360 can pre-tick
   * them in Subscription -> Add drug.
   */
  onSelectionChange?: (rxNumbers: string[]) => void;
}

interface RxDetail {
  rxDetails: any[];
  rxPln: any[];
  origRxNum: number;
  requestedRxNum: number;
}

export const AllPrescriptions: React.FC<AllPrescriptionsProps> = ({ patientId, specialtySelection, rxActions, onSelectionChange }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const activePharmacy = usePharmacyStore((state: any) => state.activePharmacy);
  // PRX-80 final review (I1) — ONE pharmacy id for every read on this tab, and
  // it is the PATIENT's: the composite id's own prefix, with the active
  // pharmacy only as the fallback for a bare patient number. This is the same
  // `parsePatientId` result the prescriptions list is already scoped by
  // (`usePatientAllPrescriptions`), so the list, the signature chips, the Hard
  // Copy and the photos can no longer disagree about whose pharmacy they are
  // reading. Rx numbers are unique per pharmacy and NOT across them, so the
  // old active-pharmacy-first derivation asked the signed-in pharmacy for
  // another pharmacy's Rx number — a different fill's hardcopy and signatures
  // — whenever staff at one pharmacy opened a patient who belongs to another.
  const { pharmacyId: patientPharmacyId } = parsePatientId(patientId, activePharmacy?.id);
  const pharmacyId = patientPharmacyId || '';

  // PRX-31: central fill is a Kroll concept (Rx.IsCF / CFHostId); Fillware
  // has no such thing, so the column would only ever show '—' there. One
  // predicate feeds the header, the cells and the empty-row colSpan.
  // PRX-54 (R5): the per-pharmacy visibility resolver now owns this — its
  // computed default for `patient360.column.cf` (visibility/visibilityCatalogue.ts)
  // reproduces the same rule (hidden on Fillware, shown on Kroll and on an
  // unknown PMS — hiding data on a missing field would be the wrong default).
  const showCfColumn = !useBlockHidden('patient360.column.cf');
  // PRX-80 Task 13: two more hideable columns (R9). The order-stage column is a
  // client-side join over this patient's orders; the specialty column is the
  // selection checkbox that feeds the "Mark as specialty" toolbar.
  const showStageColumn = !useBlockHidden('patient360.column.rx_stage');
  const specialtyColumnHidden = useBlockHidden('patient360.column.rx_specialty');
  const showSpecialtyColumn = !!specialtySelection && !specialtyColumnHidden;
  // Dispense Status: the numeric 1-8 map on Kroll, the fill-status gate on
  // Fillware (PRX-165 B1). Shown by default on both; a pharmacy can hide it.
  const showDispenseColumn = !useBlockHidden('patient360.column.rx_dispense_status');
  const databaseType: string | null = activePharmacy?.databaseType ?? null;
  // PRX-80 Task 14 (R3): the data columns are driven by ONE ordered list — the
  // head and the body render from `orderedColumns`, so they cannot drift — and
  // the user's hidden set / order lives in the same server-persisted
  // useGridSettings storage every queue uses. A pharmacy-hidden column
  // (`patient360.column.*`) is not offered at all: pharmacy visibility wins.
  // Natural order = the order GP-1225 pins: Rx Active right after RxNum, and
  // SIG right after Dispense Status — so Stage sits after SIG, never between
  // them. Chooser labels are sentence case; the RENDERED header text stays
  // Title Case ('Brand Name', 'Fill Date', …) because GP-1225 / GP-1261 match
  // it — do not "fix" the headers. `RxNum` is the one PascalCase field id and
  // is persisted per user, so it is not renamed either.
  const columnDefs = React.useMemo<TableColumnDef[]>(
    () =>
      [
        { field: 'RxNum', label: 'RxNum', alwaysVisible: true },
        { field: 'rxActive', label: 'Rx active' },
        // PRX-137: by request only, from the subscription basket (Patient 360 only).
        ...(rxActions ? [{ field: 'prn', label: 'PRN' }] : []),
        { field: 'brand', label: 'Brand name' },
        { field: 'generic', label: 'Generic name' },
        ...(showDispenseColumn ? [{ field: 'dispenseStatus', label: 'Dispense status' }] : []),
        { field: 'sig', label: 'SIG' },
        ...(showStageColumn ? [{ field: 'stage', label: 'Stage' }] : []),
        { field: 'fillDate', label: 'Fill date' },
        { field: 'dispQty', label: 'Disp qty' },
        { field: 'days', label: 'Days' },
        { field: 'remQty', label: 'Rem qty' },
        { field: 'doctor', label: 'Doctor' },
        { field: 'dueDays', label: 'Due days' },
        ...(showCfColumn ? [{ field: 'cf', label: 'CF' }] : []),
        { field: 'signatures', label: 'Signatures' },
        { field: 'hardcopy', label: 'Hardcopy' },
      ],
    [showStageColumn, showCfColumn, showDispenseColumn, !!rxActions],
  );
  const {
    orderedColumns,
    allColumnsInOrder,
    isVisible,
    setVisible,
    setOrder,
    resetToDefault,
    // The saved-view read failed, so these are the default columns — the
    // chooser says so rather than passing them off as the user's own.
    loadFailed: columnsLoadFailed,
  } = usePersistedTableColumns({
    gridKey: 'patient360:all-prescriptions',
    // Deliberately NOT the patient's pharmacy: this is the signed-in user's own
    // column layout, stored per user and pharmacy in `useGridSettings` the way
    // every queue page stores it. Keying it on the chart being viewed would
    // read and write a user's saved view under a pharmacy they are not signed
    // in to. Every PATIENT read on this tab uses `pharmacyId` above.
    pharmacyId: activePharmacy?.id || undefined,
    columns: columnDefs,
  });
  // The selection checkbox column is pharmacy-gated only (never in the chooser),
  // and so is the PRX-137 action column.
  const columnCount = orderedColumns.length + (showSpecialtyColumn ? 1 : 0) + (rxActions ? 1 : 0);
  // PRX-137: hide PMS-deleted rows, saved with the same per-user grid view.
  const { hideDeleted, setHideDeleted } = useHideDeletedPreference(
    'patient360:all-prescriptions',
    activePharmacy?.id || undefined,
  );
  const { readOnly, message: readOnlyMessage } = usePatient360ReadOnly();
  const readOnlyTitle = readOnly ? readOnlyMessage ?? undefined : undefined;
  const { data: patientOrdersData } = usePatientOrders(patientId, { enabled: showStageColumn });
  // One pass over the orders per payload, not one walk per row per render.
  const stagesByRx = React.useMemo(() => rxOrderStagesByRx(patientOrdersData?.orders), [patientOrdersData?.orders]);
  const [selectedRxNums, setSelectedRxNums] = useState<ReadonlySet<string>>(new Set());
  // The Rx numbers the LAST "Mark as specialty" run refused for having no drug
  // name. They stay selected so the toolbar can say they were not marked —
  // clearing the whole selection took the dialog's "skipped" note away before
  // anyone read it. The note is about that one run, so any deliberate
  // selection change afterwards drops it.
  const [notMarkedRxNums, setNotMarkedRxNums] = useState<ReadonlySet<string>>(new Set());
  const [specialtyDialogOpen, setSpecialtyDialogOpen] = useState(false);
  const replaceSelection = (next: ReadonlySet<string>) => {
    setNotMarkedRxNums(new Set());
    setSelectedRxNums(next);
  };
  const toggleRxSelected = (rxNum: string) => {
    if (readOnly) return;
    setNotMarkedRxNums(new Set());
    setSelectedRxNums((prev) => {
      const next = new Set(prev);
      if (next.has(rxNum)) next.delete(rxNum);
      else next.add(rxNum);
      return next;
    });
  };

  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>('all');
  const [drugNameFilter, setDrugNameFilter] = useState('');
  const [sortColumn, setSortColumn] = useState<'RxNum' | 'FillDate' | 'DrugName' | 'GenericName' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedRx, setSelectedRx] = useState<number | null>(null);
  const [rxDetail, setRxDetail] = useState<RxDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  // Which detail request is the current one. Fill History makes re-targeting a
  // single click (GP-1368), so two responses can land out of order — only the
  // newest may write state, or the dialog shows an older fill's records.
  const detailRequestRef = useRef(0);
  // Which patient/pharmacy the signature map belongs to — signatures are merged
  // now (a historical fill's are fetched on their own), so a response for a
  // chart we have left must be dropped instead of merged into the next one's.
  const signatureScope = `${pharmacyId}::${patientId}`;
  const signatureScopeRef = useRef(signatureScope);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [signatures, setSignatures] = useState<Record<string, HardcopySignature[]>>({});
  const [dialogTab, setDialogTab] = useState(TAB_HARDCOPY);
  const [counselingRecordToView, setCounselingRecordToView] = useState<CounselingRecord | null>(null);
  const [openVerificationPhoto, setOpenVerificationPhoto] = useState<PatientVerificationPhoto | null>(null);

  // PRX-68: every live product-verification photo across this patient's orders
  // at this pharmacy, fetched once per tab mount (R5 of the
  // verification-photo-everywhere plan) — the Rx-centric dialog here has no
  // order id of its own, so it asks by patient instead. The photo route filters
  // by pharmacyId AND the composite patientId, so a mismatched pharmacy returns
  // [] with no badge and no error; `pharmacyId` above is the patient's own, so
  // there is no second derivation here (PRX-80 final review, I1).
  const { data: patientVerificationPhotosData } = usePatientVerificationPhotos(pharmacyId, patientId);
  const patientVerificationPhotos = Array.isArray(patientVerificationPhotosData)
    ? patientVerificationPhotosData
    : [];
  // Rx numbers with an ITEM-scope photo on file — drives the camera icon in
  // the grid's Signatures cell. Pack-scope photos are order-wide and this
  // list carries no per-order item breakdown to attribute one to a row.
  const photoRxNumbers = React.useMemo<ReadonlySet<string>>(
    () =>
      new Set(
        patientVerificationPhotos
          .filter((p) => p.scope === 'item' && p.rxNumber)
          .map((p) => String(p.rxNumber)),
      ),
    [patientVerificationPhotos],
  );
  // Item-scope photos for the fill open in the dialog — the Rx-centric view
  // never shows a pack photo, since it has no order to say the pack covers.
  const selectedRxVerificationPhotos = React.useMemo(
    () =>
      patientVerificationPhotos.filter(
        (p) => p.scope === 'item' && String(p.rxNumber ?? '') === String(selectedRx ?? ''),
      ),
    [patientVerificationPhotos, selectedRx],
  );

  useEffect(() => {
    signatureScopeRef.current = signatureScope;
  }, [signatureScope]);

  // Reset local state when patient changes
  useEffect(() => {
    setSelectedRx(null);
    setRxDetail(null);
    setDetailError(null);
    setDialogOpen(false);
    setSignatures({});
    setDialogTab(TAB_HARDCOPY);
    setCounselingRecordToView(null);
    setOpenVerificationPhoto(null);
    setStatusFilter('all');
    setDrugNameFilter('');
    setSortColumn(null);
    setSortDirection('asc');
    // PRX-137: nothing from the last chart's actions carries over.
    setInteractionFlags(null);
    setInfoSheetRx(null);
    setSingleSpecialtyRx(null);
    setByRequestOnlyNotice(null);
    setPendingPrnPlan(null);
    setVacation(null);
    // PRX-204: ticks never carry to another chart, even on a shared Rx number.
    setSelectedRxNums(new Set());
    setNotMarkedRxNums(new Set());
  }, [patientId]);

  // Use shared hook for prescriptions - automatically caches and dedupes
  const { data: prescriptionsData, isLoading: loading, error: queryError } =
    usePatientAllPrescriptions(patientId, statusFilter);

  const prescriptions = prescriptionsData || [];
  const error = queryError ? 'Failed to load prescriptions. Please try again.' : null;

  // Filter by drug name (brand or generic)
  const filteredPrescriptions = React.useMemo(() => {
    let result = withoutDeleted(prescriptions, hideDeleted);
    if (drugNameFilter.trim()) {
      const search = drugNameFilter.trim().toLowerCase();
      result = result.filter((rx: any) =>
        (rx.BrandName && rx.BrandName.toLowerCase().includes(search)) ||
        (rx.GenericName && rx.GenericName.toLowerCase().includes(search))
      );
    }
    // Sort
    if (sortColumn) {
      result = [...result].sort((a: any, b: any) => {
        let aVal: any, bVal: any;
        if (sortColumn === 'RxNum') {
          aVal = a.RxNum || 0;
          bVal = b.RxNum || 0;
        } else if (sortColumn === 'DrugName') {
          aVal = (a.BrandName || a.GenericName || '').toLowerCase();
          bVal = (b.BrandName || b.GenericName || '').toLowerCase();
        } else if (sortColumn === 'GenericName') {
          aVal = (a.GenericName || '').toLowerCase();
          bVal = (b.GenericName || '').toLowerCase();
        } else {
          aVal = a.FillDate || '';
          bVal = b.FillDate || '';
        }
        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [prescriptions, hideDeleted, drugNameFilter, sortColumn, sortDirection]);

  // The rows handed to "Mark as specialty": the selected Rx numbers, in table order,
  // each with the drug name the row shows. No name is invented here — a row without
  // one reaches the server as-is and its validation sentence is shown verbatim.
  const selectedSpecialtyRxs = React.useMemo<MarkSpecialtyRx[]>(
    () =>
      filteredPrescriptions
        .filter((rx: any) => selectedRxNums.has(String(rx.RxNum)))
        .map((rx: any) => ({ rxNumber: String(rx.RxNum), drugName: rx.BrandName || rx.GenericName || '' })),
    [filteredPrescriptions, selectedRxNums],
  );
  const selectionNotMarked = selectedSpecialtyRxs.some((rx) => notMarkedRxNums.has(rx.rxNumber));

  // PRX-204: report the visible selection to the page; clear it on unmount.
  // The ref holds the latest callback so neither effect calls a stale one.
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;
  const selectedRxKey = selectedSpecialtyRxs.map((rx) => rx.rxNumber).join(',');
  useEffect(() => {
    onSelectionChangeRef.current?.(selectedRxKey ? selectedRxKey.split(',') : []);
  }, [selectedRxKey]);
  useEffect(() => () => onSelectionChangeRef.current?.([]), []);

  // PRX-137: the row action menu's state and reads. Both reads are keyed the
  // way the page keys them, and run only on the Patient 360 mount.
  const { membershipFor } = useRxBasketLines(rxActions?.recordPharmacyId, patientId, !!rxActions);
  const setByRequestOnly = useSetRxByRequestOnly(rxActions?.recordPharmacyId, patientId);
  // The last By request only outcome, said on the tab (success or failure).
  const [byRequestOnlyNotice, setByRequestOnlyNotice] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);
  // A billing-queue PRN write waiting for its confirmation (it moves the billing date).
  const [pendingPrnPlan, setPendingPrnPlan] = useState<Extract<ByRequestOnlyPlan, { kind: 'billingPrn' }> | null>(null);
  const { data: patientRecord } = usePatientRecord(
    rxActions ? rxActions.recordPharmacyId : undefined,
    rxActions ? patientId : undefined,
  );
  const specialtyNames = React.useMemo(() => specialtyDrugNames(patientRecord?.specialtyDrugs), [patientRecord]);
  // PRX-129: whether Mark as specialty opens PSP files is the PATIENT's pharmacy's
  // PMS (the record's), not the active pharmacy's. The same cached read Patient 360 makes.
  const { data: specialtyRecord } = usePatientRecord(
    specialtySelection ? specialtySelection.recordPharmacyId : undefined,
    specialtySelection ? patientId : undefined,
  );
  const [singleSpecialtyRx, setSingleSpecialtyRx] = useState<MarkSpecialtyRx | null>(null);
  const [infoSheetRx, setInfoSheetRx] = useState<{ din: string; drugName: string } | null>(null);
  const [interactionFlags, setInteractionFlags] = useState<Map<string, string[]> | null>(null);
  // PRX-130: the vacation supply sub-view, open with the Rx it was opened on (if any).
  const [vacation, setVacation] = useState<{ rxNumber: string | null } | null>(null);
  const hiddenDeletedCount = prescriptions.length - withoutDeleted(prescriptions, hideDeleted).length;
  const rxDin = (rx: any): string | null => (rx.DIN || rx.DrgDIN ? String(rx.DIN || rx.DrgDIN).trim() || null : null);
  const rowActionsInput = (rx: any): RxRowActionsInput => ({
    rxNumber: String(rx.RxNum),
    drugName: rx.BrandName || rx.GenericName || '',
    din: rxDin(rx),
    readOnly,
    readOnlyMessage,
    canEditBilling: !!rxActions?.canEditBilling,
    specialtyAvailable: showSpecialtyColumn,
    auditTrailAvailable: !!rxActions?.onOpenAuditTrail,
    basket: membershipFor(rx.RxNum),
  });

  // A row the filter has hidden leaves the selection: "3 selected" must mean
  // three rows the dialog will actually receive.
  useEffect(() => {
    setSelectedRxNums((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(filteredPrescriptions.map((rx: any) => String(rx.RxNum)));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [filteredPrescriptions]);

  const headerCellFor: Record<string, () => React.ReactNode> = {
    RxNum: () => (
      <React.Fragment key="RxNum">
        <TableCell sx={{ fontWeight: 700 }} sortDirection={sortColumn === 'RxNum' ? sortDirection : false}>
          <TableSortLabel
            active={sortColumn === 'RxNum'}
            direction={sortColumn === 'RxNum' ? sortDirection : 'asc'}
            onClick={() => handleSort('RxNum')}
          >
            RxNum
          </TableSortLabel>
        </TableCell>
      </React.Fragment>
    ),
    rxActive: () => (
      <React.Fragment key="rxActive">
        <TableCell sx={{ fontWeight: 700 }}>Rx Active</TableCell>
      </React.Fragment>
    ),
    dispenseStatus: () => (
      <React.Fragment key="dispenseStatus">
        <TableCell sx={{ fontWeight: 700 }}>Dispense Status</TableCell>
      </React.Fragment>
    ),
    prn: () => (
      <React.Fragment key="prn">
        <TableCell sx={{ fontWeight: 700 }}>PRN</TableCell>
      </React.Fragment>
    ),
    stage: () => (
      <React.Fragment key="stage">
        <TableCell sx={{ fontWeight: 700 }}>Stage</TableCell>
      </React.Fragment>
    ),
    brand: () => (
      <React.Fragment key="brand">
        <TableCell sx={{ fontWeight: 700 }} sortDirection={sortColumn === 'DrugName' ? sortDirection : false}>
          <TableSortLabel
            active={sortColumn === 'DrugName'}
            direction={sortColumn === 'DrugName' ? sortDirection : 'asc'}
            onClick={() => handleSort('DrugName')}
          >
            Brand Name
          </TableSortLabel>
        </TableCell>
      </React.Fragment>
    ),
    generic: () => (
      <React.Fragment key="generic">
        <TableCell sx={{ fontWeight: 700 }} sortDirection={sortColumn === 'GenericName' ? sortDirection : false}>
          <TableSortLabel
            active={sortColumn === 'GenericName'}
            direction={sortColumn === 'GenericName' ? sortDirection : 'asc'}
            onClick={() => handleSort('GenericName')}
          >
            Generic Name
          </TableSortLabel>
        </TableCell>
      </React.Fragment>
    ),
    sig: () => (
      <React.Fragment key="sig">
        <TableCell sx={{ fontWeight: 700 }}>SIG</TableCell>
      </React.Fragment>
    ),
    fillDate: () => (
      <React.Fragment key="fillDate">
        <TableCell sx={{ fontWeight: 700 }} sortDirection={sortColumn === 'FillDate' ? sortDirection : false}>
          <TableSortLabel
            active={sortColumn === 'FillDate'}
            direction={sortColumn === 'FillDate' ? sortDirection : 'asc'}
            onClick={() => handleSort('FillDate')}
          >
            Fill Date
          </TableSortLabel>
        </TableCell>
      </React.Fragment>
    ),
    dispQty: () => (
      <React.Fragment key="dispQty">
        <TableCell sx={{ fontWeight: 700 }}>Disp Qty</TableCell>
      </React.Fragment>
    ),
    days: () => (
      <React.Fragment key="days">
        <TableCell sx={{ fontWeight: 700 }}>Days</TableCell>
      </React.Fragment>
    ),
    remQty: () => (
      <React.Fragment key="remQty">
        <TableCell sx={{ fontWeight: 700 }}>Rem Qty</TableCell>
      </React.Fragment>
    ),
    doctor: () => (
      <React.Fragment key="doctor">
        <TableCell sx={{ fontWeight: 700 }}>Doctor</TableCell>
      </React.Fragment>
    ),
    dueDays: () => (
      <React.Fragment key="dueDays">
        <TableCell sx={{ fontWeight: 700 }}>Due Days</TableCell>
      </React.Fragment>
    ),
    cf: () => (
      <React.Fragment key="cf">
        <TableCell sx={{ fontWeight: 700 }}>CF</TableCell>
      </React.Fragment>
    ),
    signatures: () => (
      <React.Fragment key="signatures">
        <TableCell sx={{ fontWeight: 700 }}>Signatures</TableCell>
      </React.Fragment>
    ),
    hardcopy: () => (
      <React.Fragment key="hardcopy">
        <TableCell sx={{ fontWeight: 700 }}>Hardcopy</TableCell>
      </React.Fragment>
    ),
  };

  const bodyCellFor: Record<string, (rx: any) => React.ReactNode> = {
    RxNum: (rx: any) => (
      <React.Fragment key="RxNum">
        <TableCell>
          {/* PRX-137 review (M4): the interaction flag sits on RxNum, the one
              column that can never be hidden. */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2" fontFamily="monospace" fontWeight="600">
              {rx.RxNum}
            </Typography>
            {interactionFlags?.get(String(rx.RxNum)) && (
              <InteractionFlag reasons={interactionFlags.get(String(rx.RxNum)) as string[]} />
            )}
          </Stack>
        </TableCell>
      </React.Fragment>
    ),
    rxActive: (rx: any) => <RxActiveStatusCell key="rxActive" rxStatusWord={rx.rxStatusWord ?? null} rxStatusReason={rx.rxStatusReason ?? null} inactive={Boolean(rx.Inactive)} status={rx.Status} adjState={rx.AdjState} databaseType={databaseType} />,
    dispenseStatus: (rx: any) => <DispenseStatusCell key="dispenseStatus" rxStatusWord={rx.rxStatusWord ?? null} rxStatusReason={rx.rxStatusReason ?? null} inactive={Boolean(rx.Inactive)} status={rx.Status} adjState={rx.AdjState} databaseType={databaseType} pmsGate={rx.pmsGate ?? null} />,
    prn: (rx: any) => <PrnCell key="prn" membership={membershipFor(rx.RxNum)} />,
    stage: (rx: any) => (
      <React.Fragment key="stage">
        <TableCell data-testid="rx-stage-cell">{rxOrderStageLabel(stagesByRx.get(String(rx.RxNum)) ?? [])}</TableCell>
      </React.Fragment>
    ),
    brand: (rx: any) => (
      <React.Fragment key="brand">
        <TableCell>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2" fontWeight="600">
              {rx.BrandName || '—'}
            </Typography>
            {(rx.DrgRefrigerated === true || rx.DrgRefrigerated === 1) && (
              <AcUnitIcon sx={{ fontSize: '0.85rem', color: 'info.main' }} titleAccess="Refrigerated - Cold Chain" />
            )}
            {rxActions && isSpecialtyRx(rx, specialtyNames) && <SpecialtyBadge />}
          </Stack>
        </TableCell>
      </React.Fragment>
    ),
    generic: (rx: any) => (
      <React.Fragment key="generic">
        <TableCell>
          <Typography variant="body2">
            {rx.GenericName || '—'}
          </Typography>
        </TableCell>
      </React.Fragment>
    ),
    sig: (rx: any) => (
      <React.Fragment key="sig">
        <TableCell>
          <Typography variant="body2" sx={{ maxWidth: 260, whiteSpace: 'normal' }}>
            {rx.SIG || '—'}
          </Typography>
        </TableCell>
      </React.Fragment>
    ),
    fillDate: (rx: any) => (
      <React.Fragment key="fillDate">
        <TableCell>{formatDate(rx.FillDate)}</TableCell>
      </React.Fragment>
    ),
    dispQty: (rx: any) => (
      <React.Fragment key="dispQty">
        <TableCell>{rx.DispQty}</TableCell>
      </React.Fragment>
    ),
    days: (rx: any) => (
      <React.Fragment key="days">
        <TableCell>{rx.DaysSupply}</TableCell>
      </React.Fragment>
    ),
    remQty: (rx: any) => (
      <React.Fragment key="remQty">
        <TableCell>{rx.RemQty}</TableCell>
      </React.Fragment>
    ),
    doctor: (rx: any) => (
      <React.Fragment key="doctor">
        <TableCell>{rx.DocName || '—'}</TableCell>
      </React.Fragment>
    ),
    // PRX-137: Fillware now sends DueDays too; a null there says why. A
    // by-request-only Rx has no due date there (PRX-151).
    dueDays: (rx: any) => (
      <DueDaysCell
        key="dueDays"
        dueDays={rx.DueDays}
        databaseType={databaseType}
        byRequestOnly={isByRequestOnly(membershipFor(rx.RxNum))}
      />
    ),
    cf: (rx: any) => (
      <React.Fragment key="cf">
        <TableCell>
          {rx.IsCF ? (
            <Chip label="CF" size="small" color="info" />
          ) : '—'}
        </TableCell>
      </React.Fragment>
    ),
    signatures: (rx: any) => (
      <React.Fragment key="signatures">
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{ gap: 0.5 }}>
            {(() => {
              const rxSignatures = signatures[String(rx.RxNum)];
              return rxSignatures && rxSignatures.length > 0
                ? rxSignatures.map((sig) => (
                    <Chip
                      key={sig.id}
                      label={sig.initials}
                      size="small"
                      color="success"
                      title={`${sig.signatureType.replace(/_/g, ' ')} - ${sig.userName} - ${
                        sig.signedAt
                          ? new Date(
                              typeof sig.signedAt === 'string'
                                ? sig.signedAt
                                : sig.signedAt.toISOString()
                            ).toLocaleDateString()
                          : 'Unknown'
                      }`}
                      sx={{
                        bgcolor: tintSuccessCard,
                        borderColor: 'success.main',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                  ))
                : '—';
            })()}
            {/* PRX-68: a product-verification photo was captured for this Rx. */}
            {photoRxNumbers.has(String(rx.RxNum)) && (
              <CameraAltIcon
                fontSize="small"
                color="action"
                titleAccess="Product verification photo"
              />
            )}
          </Stack>
        </TableCell>
      </React.Fragment>
    ),
    hardcopy: (rx: any) => (
      <React.Fragment key="hardcopy">
        <TableCell>
          <IconButton
            size="small"
            color="primary"
            title="View Hardcopy"
            onClick={(e) => {
              e.stopPropagation();
              setDialogTab(TAB_HARDCOPY);
              fetchRxDetail(rx.RxNum);
            }}
          >
            <DescriptionIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </React.Fragment>
    ),
  };

  const handleSort = (column: 'RxNum' | 'FillDate' | 'DrugName' | 'GenericName') => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Fetch signatures for a set of Rx numbers. `merge` keeps what is already
  // known (GP-1368: a historical fill opened from Fill History is not in the
  // grid, so its signatures are fetched on demand and must not wipe the grid's).
  const fetchSignaturesForRxNumbers = async (rxNums: Array<number | string>, merge = false) => {
    if (!pharmacyId || rxNums.length === 0) return;

    // Rx numbers are unique per pharmacy, not across them — a response that
    // arrives after the chart moved on belongs to the patient it was asked for.
    const scope = signatureScope;
    try {
      const rxNumbers = rxNums.map((rxNum) => String(rxNum)).join(',');
      const response = await api.get(
        `/api/rx/${pharmacyId}/signatures-batch?rxNumbers=${encodeURIComponent(rxNumbers)}`
      );

      if (signatureScopeRef.current !== scope) return;
      if (response.data?.success) {
        const fetchedSignatures = response.data.data?.signatures || [];

        // Every Rx we asked about starts empty, so "asked, and this Rx has
        // none" is recorded as none rather than reading as "not looked up yet".
        const signaturesByRx: Record<string, HardcopySignature[]> = {};
        rxNums.forEach((rxNum) => {
          signaturesByRx[String(rxNum)] = [];
        });
        fetchedSignatures.forEach((sig: HardcopySignature) => {
          const rxNum = String(sig.rxNumber);
          if (!signaturesByRx[rxNum]) {
            signaturesByRx[rxNum] = [];
          }
          signaturesByRx[rxNum].push(sig);
        });

        setSignatures((prev) => (merge ? { ...prev, ...signaturesByRx } : signaturesByRx));
      }
    } catch (error) {
      console.error('Failed to fetch signatures:', error);
      // Silent fail - signatures are optional
    }
  };

  /**
   * Point the dialog at one fill and load ITS detail.
   *
   * `rx-detail` answers for the Rx it was asked about: `rxDetails` is the whole
   * OrigRxNum series, but `rxPln` (insurance / adjudication) is scoped to the
   * requested RxNum alone. So re-targeting has to refetch — reusing the previous
   * fill's response would put another fill's payments under this Rx's title.
   */
  const loadRxDetail = async (rxNum: number) => {
    if (!pharmacyId) return;

    const requestId = ++detailRequestRef.current;
    setDetailLoading(true);
    setDetailError(null);
    setSelectedRx(rxNum);

    try {
      const response = await api.get(`/api/rx/${pharmacyId}/rx-detail/${rxNum}`);
      if (detailRequestRef.current !== requestId) return; // a newer fill was opened
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to load prescription detail');
      }
      setRxDetail(response.data.data);
    } catch (error: any) {
      if (detailRequestRef.current !== requestId) return;
      console.error('Failed to fetch Rx detail:', error);
      // Never keep the previous fill's series and insurance on screen under this
      // Rx's title — say the lookup failed instead of showing another fill's
      // adjudication as if it were this one's.
      setRxDetail(null);
      setDetailError(
        error.response?.data?.message || error.message || 'Failed to load prescription detail',
      );
    } finally {
      if (detailRequestRef.current === requestId) setDetailLoading(false);
    }
  };

  // Open the dialog from a grid row (or its Hardcopy button).
  const fetchRxDetail = (rxNum: number) => {
    setDialogOpen(true);
    setDialogTab(TAB_HARDCOPY); // Start on hardcopy tab
    loadRxDetail(rxNum);
  };

  /** PRX-137: send a By request only change and say how it went. */
  const runByRequestOnly = (plan: Exclude<ByRequestOnlyPlan, { kind: 'disabled' }>) => {
    const rxNumber = plan.line.rxNumber;
    const turnedOn = plan.kind === 'blisterAskFirst' || plan.next;
    setByRequestOnly.mutate(plan, {
      onSuccess: () =>
        setByRequestOnlyNotice({
          severity: 'success',
          text: turnedOn ? `Rx ${rxNumber} is now by request only.` : `Rx ${rxNumber} is no longer by request only.`,
        }),
      onError: (error) =>
        setByRequestOnlyNotice({
          severity: 'error',
          text: `Couldn't change By request only for Rx ${rxNumber}. ${apiErrorMessage(error, 'The change was not saved.')}`,
        }),
    });
  };

  /** PRX-137: one row action. Every write here is PHOX's own; nothing goes to the PMS. */
  const handleRowAction = (rx: any, key: RxRowActionKey) => {
    const rxNumber = String(rx.RxNum);
    const openOn = (tab: number) => {
      setDialogOpen(true);
      setDialogTab(tab);
      loadRxDetail(rx.RxNum);
    };
    switch (key) {
      case 'specialty':
        setSingleSpecialtyRx({ rxNumber, drugName: rx.BrandName || rx.GenericName || '' });
        setSpecialtyDialogOpen(true);
        return;
      case 'byRequestOnly': {
        const plan = byRequestOnlyPlan(rowActionsInput(rx));
        if (plan.kind === 'disabled') return;
        setByRequestOnlyNotice(null);
        // The PRN route moves the subscription's billing date: ask first.
        if (plan.kind === 'billingPrn') setPendingPrnPlan(plan);
        else runByRequestOnly(plan);
        return;
      }
      case 'infoSheet': {
        const din = rxDin(rx);
        if (din) setInfoSheetRx({ din, drugName: rx.BrandName || rx.GenericName || '' });
        return;
      }
      case 'auditTrail':
        rxActions?.onOpenAuditTrail?.(rxNumber);
        return;
      case 'rxProfile':
        openOn(TAB_FILL_HISTORY);
        return;
      case 'billing':
        openOn(TAB_INSURANCE);
        return;
      case 'images':
        openOn(TAB_HARDCOPY);
        return;
      case 'vacationSupply':
        setVacation({ rxNumber });
        return;
      default:
        // The deferred actions are disabled in the menu and never reach here.
        return;
    }
  };

  /**
   * GP-1368 — re-target the open dialog at another fill in the same series.
   *
   * Every tab then answers for THAT fill: the hardcopy, the scanned Rx image,
   * the counseling lookup, and the insurance rows the refetch brings back.
   * Signatures are grid-scoped, so a fill we've never asked about is looked up
   * now rather than rendering an empty signature strip.
   */
  const handleOpenFillHardcopy = (rxNum: number) => {
    setDialogTab(TAB_HARDCOPY);
    if (signatures[String(rxNum)] === undefined) {
      fetchSignaturesForRxNumbers([rxNum], true);
    }
    loadRxDetail(rxNum);
  };

  // Fetch signatures when prescriptions change. Merged, not replaced: a
  // historical fill opened from Fill History (GP-1368) is not a grid row, and a
  // grid refresh must not silently blank the signature strip it is showing.
  useEffect(() => {
    if (prescriptions.length > 0) {
      fetchSignaturesForRxNumbers(prescriptions.map((rx: any) => rx.RxNum), true);
    }
  }, [prescriptions, pharmacyId]);

  // GP-1358: only tear the dialog's content down AFTER the close transition has
  // finished — clearing `selectedRx` on click made the title flash
  // "Rx Detail - #" (no number) over the fading dialog.
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleDialogExited = () => {
    setRxDetail(null);
    setDetailError(null);
    setSelectedRx(null);
    setDialogTab(TAB_HARDCOPY);
    // The full counseling session dialog is a SIBLING of this dialog — left
    // open across the parent's exit it would outlive the Rx it belongs to.
    setCounselingRecordToView(null);
    setOpenVerificationPhoto(null);
  };

  // GP-1358: counseling for this patient — the same payload the chart's RX
  // History / Counseling History tabs already load, so the dialog reads it from
  // cache. Fetched with the grid (not on dialog open) so the Counseling tab has
  // content the moment a prescription is opened.
  const {
    data: patientCounseling = [],
    isLoading: counselingLoading,
    isError: counselingError,
  } = usePatientCounselingRecords(patientId);

  // Only this prescription's sessions — a counseling documents the drug it was
  // about, so records carrying another Rx number are never shown as this Rx's
  // (this is also what prints on its hardcopy).
  const counselingForSelectedRx = React.useMemo(
    () => filterCounselingForRx(patientCounseling, selectedRx),
    [patientCounseling, selectedRx],
  );

  // Every Rx number in the open prescription's fill series (Kroll's OrigRxNum
  // chain) — the same rows the Fill History tab lists.
  const fillSeriesRxNums = React.useMemo<number[]>(
    () => (rxDetail?.rxDetails || []).map((fill: any) => fill.RxNum).filter((n: any) => n != null),
    [rxDetail],
  );

  /**
   * GP-1368 — counseling recorded on the OTHER fills of this medication.
   *
   * A drug filled thirty times is thirty Rx numbers, and the counseling sits on
   * whichever fill the pharmacist did it on. Showing only the fill in hand made
   * a counselled medication read "No counseling record for this prescription".
   * These are kept SEPARATE from `counselingForSelectedRx` (which is what the
   * hardcopy prints) and each is labelled with the Rx it was recorded against —
   * borrowing another fill's session onto this Rx's printed sheet would be a
   * clinical claim we can't make.
   */
  const counselingOnOtherFills = React.useMemo(
    () =>
      filterCounselingForRxNumbers(
        patientCounseling,
        fillSeriesRxNums.filter((rxNum) => String(rxNum) !== String(selectedRx)),
      ),
    [patientCounseling, fillSeriesRxNums, selectedRx],
  );

  /** The other-fill sessions grouped under the Rx number that holds them, newest Rx first. */
  const counselingByOtherRx = React.useMemo(() => {
    const groups = new Map<string, CounselingRecord[]>();
    counselingOnOtherFills.forEach((rec) => {
      const key = String(rec.rxNumber);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(rec);
    });
    return [...groups.entries()].sort((a, b) => Number(b[0]) - Number(a[0]));
  }, [counselingOnOtherFills]);

  // Counsellor auth0 ids → display names, for both the card and the printed
  // hardcopy block (an auth0 id on a printed sheet is unreadable). Covers the
  // other-fill sessions too — those cards are handed the same resolver, so an
  // id left out of the batch would render raw (GP-1368).
  const counselingUserIds = React.useMemo(
    () =>
      [...counselingForSelectedRx, ...counselingOnOtherFills].flatMap((r) => [
        r.completedBy,
        r.createdBy,
      ]),
    [counselingForSelectedRx, counselingOnOtherFills],
  );
  const { userNames: counselingUserMap } = useBatchUserLookup(counselingUserIds);
  const displayUser = React.useCallback(
    (value: string | undefined | null): string => (value ? counselingUserMap.get(value) || value : ''),
    [counselingUserMap],
  );

  // The counseling as it prints alongside the hardcopy, plus the counsel
  // checkboxes the session implies (GP-1110: "by phone" ticks Via Phone).
  const hardcopyCounseling = React.useMemo(
    () => toHardcopyCounselingEntries(counselingForSelectedRx, displayUser),
    [counselingForSelectedRx, displayUser],
  );
  // Latest session for this Rx picked through the shared helper — the same path
  // RX History's hardcopy takes, so the two sheets can't tick different boxes.
  const hardcopyCounselFlags = React.useMemo(
    () => deriveCounselFlagsFromCounseling(pickCounselingRecordForRx(counselingForSelectedRx, selectedRx)),
    [counselingForSelectedRx, selectedRx],
  );

  // GP-1358: editing or completing a session from the Counseling tab must not
  // leave the card, the tab count, the printed block and the counsel checkboxes
  // showing the pre-edit record — they all read this one query.
  const refreshCounseling = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['patient', 'counseling', patientId] });
  }, [queryClient, patientId]);

  // Single-medication array for the shared RxImagePanel (the scanned script-image
  // viewer) shown alongside the Kroll hardcopy. Status drives the RxStatusBadge.
  const selectedRxMedications = React.useMemo<RxImagePanelMedication[]>(() => {
    if (selectedRx == null) return [];
    // A fill opened from Fill History (GP-1368) is not one of the grid rows, so
    // its status comes off the series row instead.
    const gridRow = prescriptions.find((rx: any) => rx.RxNum === selectedRx);
    const detailRow = rxDetail?.rxDetails?.find((fill: any) => fill.RxNum === selectedRx);
    const status = gridRow?.Status ?? detailRow?.Status;
    const rxAdjState = gridRow?.AdjState ?? detailRow?.AdjState ?? null;
    return [{ rxNumber: String(selectedRx), status, rxAdjState }];
  }, [selectedRx, prescriptions, rxDetail]);

  /**
   * What the Fill History / Insurance / Details tabs show when the detail
   * lookup failed. They all read one `rx-detail` response, so one message —
   * "we could not look", never a blank that reads as "there is nothing".
   */
  const renderDetailError = () => (
    <Alert
      severity="error"
      data-testid="rx-detail-load-error"
      action={
        <Button
          color="inherit"
          size="small"
          onClick={() => selectedRx != null && loadRxDetail(selectedRx)}
        >
          Retry
        </Button>
      }
    >
      Couldn't load the detail for RX #{selectedRx}. {detailError}
    </Alert>
  );

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    try {
      // Extract just the date part (YYYY-MM-DD) to avoid timezone shifts
      const datePart = String(dateStr).split('T')[0];
      const [year, month, day] = datePart.split('-');
      if (!year || !month || !day) return '—';
      return `${day}/${month}/${year}`;
    } catch {
      return '—';
    }
  };

  if (vacation && rxActions) {
    return (
      <VacationSupplyView
        pharmacyId={rxActions.recordPharmacyId}
        patientId={patientId}
        initialRxNumber={vacation.rxNumber}
        onBack={() => setVacation(null)}
      />
    );
  }

  return (
    <Box>
      {/* PRX-84: one row — filter, search, count. The section heading, its
          icon and the "click any row" hint are gone: the tab strip already
          names the list and the rows are visibly clickable.
          PRX-82 rework: this row scrolls away with the page — only the table
          header row below stays pinned. Not sticky, so no top offset or
          z-index is needed here. */}
      <Box
        data-testid="all-prescriptions-toolbar"
        sx={{
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          pb: 1,
        }}
      >
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={(e, value) => value && setStatusFilter(value)}
          size="small"
        >
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="inactive">Inactive</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small"
          placeholder="Filter by drug name (brand or generic)..."
          value={drugNameFilter}
          onChange={(e) => setDrugNameFilter(e.target.value)}
          sx={{ width: 360, maxWidth: '100%' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <Typography
          data-testid="all-prescriptions-count"
          variant="body2"
          color="text.secondary"
          sx={{ ml: 'auto' }}
        >
          {filteredPrescriptions.length}
          {drugNameFilter.trim() || hiddenDeletedCount > 0 ? ` of ${prescriptions.length}` : ''}
          {filteredPrescriptions.length === 1 ? ' prescription' : ' prescriptions'}
        </Typography>
        {/* PRX-137: hide deleted, the status legend and the interaction check. */}
        <HideDeletedToggle databaseType={databaseType} checked={hideDeleted} onChange={setHideDeleted} />
        <RxStatusLegend databaseType={databaseType} />
        {rxActions && pharmacyId && (
          <RxInteractionsChip
            key={patientId}
            pharmacyId={pharmacyId}
            patientId={patientId}
            rows={prescriptions}
            onFlags={setInteractionFlags}
          />
        )}
        {/* PRX-145 WP-20: the CKD renal-dosing flag, from the Kidney function labs. */}
        {rxActions && pharmacyId && <RxRenalDosingChip pharmacyId={pharmacyId} patientId={patientId} />}
        {rxActions && (
          <Button size="small" variant="outlined" onClick={() => setVacation({ rxNumber: null })}>
            Vacation supply
          </Button>
        )}
        <TableColumnChooser
          columns={allColumnsInOrder}
          isVisible={isVisible}
          setVisible={setVisible}
          setOrder={setOrder}
          resetToDefault={resetToDefault}
          loadFailed={columnsLoadFailed}
        />
      </Box>

      {showSpecialtyColumn && selectedSpecialtyRxs.length > 0 && (
        <Box data-testid="specialty-selection-toolbar" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body2">
            {selectionNotMarked
              ? `${selectedSpecialtyRxs.length} selected — not marked: ${NO_DRUG_NAME_REASON}`
              : `${selectedSpecialtyRxs.length} selected`}
          </Typography>
          <Button
            size="small"
            variant="contained"
            disabled={readOnly}
            title={readOnlyTitle}
            onClick={() => setSpecialtyDialogOpen(true)}
          >
            Mark as specialty
          </Button>
          <Button size="small" onClick={() => replaceSelection(new Set())}>
            Clear selection
          </Button>
        </Box>
      )}
      {specialtySelection && (
        <MarkSpecialtyDialog
          open={specialtyDialogOpen}
          pharmacyId={specialtySelection.recordPharmacyId}
          patientId={patientId || ''}
          prescriptions={singleSpecialtyRx ? [singleSpecialtyRx] : selectedSpecialtyRxs}
          // PRX-129: the PSP workflow runs where the PATIENT's pharmacy is Fillware (the record's PMS, not the active pharmacy's).
          // Unknown (loading, failed, or no PMS on the record) stays undefined: the dialog sends nothing.
          pspEnabled={specialtyRecord?.databaseType ? specialtyRecord.databaseType === 'fillware' : undefined}
          readOnly={readOnly}
          readOnlyMessage={readOnlyMessage}
          onClose={() => {
            setSpecialtyDialogOpen(false);
            setSingleSpecialtyRx(null);
          }}
          onMarked={(skippedRxNumbers) => {
            setSpecialtyDialogOpen(false);
            // PRX-137: a single Rx marked from its row menu leaves the
            // checkbox selection alone.
            if (singleSpecialtyRx) {
              setSingleSpecialtyRx(null);
              return;
            }
            // Keep exactly the rows the dialog refused, and say why above them.
            // The dialog refreshes the record read itself (it knows how many rows it created).
            const refused = new Set(skippedRxNumbers);
            setSelectedRxNums(refused);
            setNotMarkedRxNums(refused);
          }}
        />
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {byRequestOnlyNotice && (
        <Alert severity={byRequestOnlyNotice.severity} sx={{ mb: 1 }} onClose={() => setByRequestOnlyNotice(null)}>
          {byRequestOnlyNotice.text}
        </Alert>
      )}
      <ByRequestOnlyConfirmDialog
        rxNumber={pendingPrnPlan?.line.rxNumber ?? null}
        turningOn={pendingPrnPlan?.next ?? true}
        onCancel={() => setPendingPrnPlan(null)}
        onConfirm={() => {
          const plan = pendingPrnPlan;
          setPendingPrnPlan(null);
          if (plan) runByRequestOnly(plan);
        }}
      />
      {infoSheetRx && (
        <RxInfoSheetDialog
          open
          onClose={() => setInfoSheetRx(null)}
          din={infoSheetRx.din}
          drugName={infoSheetRx.drugName}
        />
      )}

      {/* Prescriptions Table */}
      {/* PRX-82: overflow visible on purpose — TableContainer's default
          overflow-x:auto would make this box the sticky scrollport and the
          header would never pin. Horizontal overflow spills into the tab
          panel's own scroll instead. */}
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflow: 'visible' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead
              data-testid="all-prescriptions-table-head"
              sx={{ '& th': { bgcolor: stickyHeaderSurface(theme), top: 0 } }}
            >
              <TableRow>
                {showSpecialtyColumn && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      inputProps={{ 'aria-label': 'Select all prescriptions' }}
                      disabled={readOnly || filteredPrescriptions.length === 0}
                      title={readOnlyTitle}
                      checked={filteredPrescriptions.length > 0 && filteredPrescriptions.every((rx: any) => selectedRxNums.has(String(rx.RxNum)))}
                      indeterminate={selectedRxNums.size > 0 && !filteredPrescriptions.every((rx: any) => selectedRxNums.has(String(rx.RxNum)))}
                      onChange={(e) => {
                        if (readOnly) return;
                        replaceSelection(e.target.checked ? new Set(filteredPrescriptions.map((rx: any) => String(rx.RxNum))) : new Set());
                      }}
                    />
                  </TableCell>
                )}
                {orderedColumns.map((c) => headerCellFor[c.field]())}
                {rxActions && (
                  <TableCell padding="checkbox" aria-label="Actions" />
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPrescriptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columnCount} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {drugNameFilter.trim()
                        ? `No prescriptions matching "${drugNameFilter.trim()}"`
                        : `No ${statusFilter !== 'all' ? statusFilter : ''} prescriptions found`}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPrescriptions.map((rx: any) => (
                  <TableRow
                    key={rx.RxNum}
                    hover
                    onClick={() => fetchRxDetail(rx.RxNum)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) } }}
                  >
                    {showSpecialtyColumn && (
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          inputProps={{ 'aria-label': `Select Rx ${rx.RxNum}` }}
                          disabled={readOnly}
                          title={readOnlyTitle}
                          checked={selectedRxNums.has(String(rx.RxNum))}
                          onChange={() => toggleRxSelected(String(rx.RxNum))}
                        />
                      </TableCell>
                    )}
                    {orderedColumns.map((c) => bodyCellFor[c.field](rx))}
                    {rxActions && (
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <RxRowActionsMenu
                          rxNumber={String(rx.RxNum)}
                          actions={buildRxRowActions(rowActionsInput(rx))}
                          onAction={(key) => handleRowAction(rx, key)}
                          busy={
                            setByRequestOnly.isPending &&
                            setByRequestOnly.variables?.line.rxNumber === String(rx.RxNum)
                          }
                        />
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Rx Detail Dialog with Tabs */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullScreen
        TransitionProps={{ onExited: handleDialogExited }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            Rx Detail - #{selectedRx}
          </Box>
          <IconButton onClick={handleCloseDialog} size="small" aria-label="Close dialog">
            <CloseIcon aria-hidden="true" />
          </IconButton>
        </DialogTitle>
        <Divider />

        {/* Tabs */}
        <Tabs value={dialogTab} onChange={(e, v) => setDialogTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Hardcopy" />
          <Tab
            icon={<RecordVoiceOverIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            /* GP-1368: an unbadged "Counseling" tab reads as "never counselled",
               so when this fill has none but an earlier one does, say so. */
            label={
              counselingForSelectedRx.length > 0
                ? `Counseling (${counselingForSelectedRx.length})`
                : counselingOnOtherFills.length > 0
                  ? `Counseling (${counselingOnOtherFills.length} on other fills)`
                  : 'Counseling'
            }
            data-testid="rx-detail-counseling-tab"
          />
          <Tab label="Fill History" />
          <Tab label="Insurance" />
          <Tab label="Details" />
          <Tab icon={<NoteAltIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Notes" />
        </Tabs>

        <DialogContent>
          {/* Tab 0: scanned Rx script image (left) + Kroll hardcopy receipt
              (right), side-by-side like the Clinical Verification layout. */}
          {dialogTab === TAB_HARDCOPY && selectedRx && (
            <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 180px)', minHeight: 400 }}>
              {/* Left: scanned Rx image / hard copy (PDF, TIFF, or image) */}
              <Box
                sx={{
                  width: '42%',
                  minWidth: 360,
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <RxImagePanel
                  patientId={patientId}
                  pharmacyId={pharmacyId}
                  medications={selectedRxMedications}
                  activeDrugIndex={0}
                  resetKey={selectedRx ?? undefined}
                />
              </Box>
              {/* Right: Kroll-rendered hardcopy receipt */}
              <Box sx={{ flex: 1, minWidth: 0, height: '100%', overflow: 'auto' }}>
                <HardcopyViewer
                  rxNumber={String(selectedRx)}
                  pharmacyId={pharmacyId}
                  signatures={signatures[String(selectedRx)] || []}
                  editable={false}
                  // GP-1358: counseling prints WITH the hardcopy — the printable
                  // artifact now carries who counselled, how, when, and the
                  // checklist score, instead of dropping it.
                  counselingRecords={hardcopyCounseling}
                  counselFlags={hardcopyCounselFlags}
                  verificationPhotos={selectedRxVerificationPhotos}
                  onOpenVerificationPhoto={(photo) =>
                    setOpenVerificationPhoto(photo as PatientVerificationPhoto)
                  }
                />
              </Box>
            </Box>
          )}

          {/* Tab 1: Counseling — the same card the RX History Rx card renders
              (GP-1358), scoped to this prescription. */}
          {dialogTab === TAB_COUNSELING && selectedRx && (
            <Box sx={{ minHeight: 300, maxWidth: 720 }} data-testid="rx-detail-counseling-panel">
              <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                Counseling — RX #{selectedRx}
              </Typography>
              {counselingLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : counselingError ? (
                /* Never let a failed fetch read as "this Rx was not counselled" —
                   that is a clinical claim, and the pharmacist must know the
                   difference between "no record" and "we could not look". */
                <Alert severity="error">
                  Couldn't load counseling for this prescription. Refresh to try again.
                </Alert>
              ) : (
                <>
                  <RxCounselingCard
                    records={counselingForSelectedRx}
                    resolveUser={displayUser}
                    showHeader={false}
                    emptyText={`No counseling record on RX #${selectedRx}`}
                    onOpenRecord={(rec) => setCounselingRecordToView(rec)}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                    This counseling is also printed with the hardcopy — use the print button on the
                    Hardcopy tab.
                  </Typography>

                  {/* The fill series is what tells us which OTHER Rx numbers to
                      look under, so without it "none on other fills" would be a
                      claim we can't make (GP-1368). */}
                  {detailError && (
                    <Alert severity="warning" sx={{ mt: 2 }} data-testid="rx-detail-counseling-series-unknown">
                      Couldn't load this prescription's fill series, so counseling recorded on its
                      other fills could not be checked.
                    </Alert>
                  )}

                  {/* GP-1368: the same medication counselled on an earlier fill.
                      Shown under the Rx that actually holds each session — never
                      merged into this Rx's record, and never printed on its
                      hardcopy. */}
                  {counselingByOtherRx.length > 0 && (
                    <Box sx={{ mt: 3 }} data-testid="rx-detail-counseling-other-fills">
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="subtitle2" fontWeight={700}>
                        Counseling on other fills of this prescription ({counselingOnOtherFills.length})
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                        Recorded against a different Rx number in the same fill series.
                      </Typography>
                      {counselingByOtherRx.map(([rxNum, records]) => (
                        <Box key={rxNum} sx={{ mb: 2 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                            <Chip label={`RX #${rxNum}`} size="small" color="primary" variant="outlined" />
                            <Button
                              size="small"
                              startIcon={<DescriptionIcon sx={{ fontSize: 14 }} />}
                              aria-label={`Open fill Rx ${rxNum}`}
                              onClick={() => handleOpenFillHardcopy(Number(rxNum))}
                            >
                              Open this fill
                            </Button>
                          </Stack>
                          <RxCounselingCard
                            records={records}
                            resolveUser={displayUser}
                            showHeader={false}
                            onOpenRecord={(rec) => setCounselingRecordToView(rec)}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}

          {/* Tab 2: Fill History — same table the one-click Fill History dialog
              renders in Clinical Verification (GP-1038), so the two never drift. */}
          {dialogTab === TAB_FILL_HISTORY && (
            detailLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : detailError ? (
              renderDetailError()
            ) : rxDetail ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                  Click any fill to open its own hardcopy and Rx image
                </Typography>
                <RxFillHistoryTable
                  fills={rxDetail.rxDetails}
                  highlightRxNum={selectedRx}
                  /* GP-1368: every fill in the series is reachable, not just the
                     newest Rx the dialog was opened on. */
                  onOpenHardcopy={handleOpenFillHardcopy}
                />
              </>
            ) : (
              <Typography color="text.secondary">No detail available</Typography>
            )
          )}

          {/* Tab 3: Insurance — Kroll scopes RxPln to ONE fill, so this is the
              adjudication of the Rx in the title and no other (GP-1368). */}
          {dialogTab === TAB_INSURANCE && (
            detailLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : detailError ? (
              renderDetailError()
            ) : rxDetail && rxDetail.rxPln.length > 0 ? (
              <TableContainer>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Insurance — RX #{selectedRx}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: panelNeutral }}>
                      <TableCell sx={{ fontWeight: 700 }}>Seq</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Plan Code</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Pays</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Tran Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Adj State</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>SSC</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Reason Code</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Adj Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rxDetail.rxPln.map((pln: any, idx: number) => (
                      <TableRow key={idx}>
                        <TableCell>{pln.A_Seq}</TableCell>
                        <TableCell>
                          <Chip
                            label={pln.A_SubPlanCode || 'N/A'}
                            size="small"
                            color={pln.A_SubPlanCode === 'Cash' ? 'default' : 'primary'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell><Typography fontWeight="600">${(pln.A_Pays || 0).toFixed(2)}</Typography></TableCell>
                        <TableCell>{pln.A_TranType}</TableCell>
                        <TableCell>
                          <Chip
                            label={pln.A_AdjState === 4 ? 'Paid' : pln.A_AdjState}
                            size="small"
                            color={pln.A_AdjState === 4 ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{pln.A_SSC || '—'}</TableCell>
                        <TableCell>{pln.A_ReasonCode || '—'}</TableCell>
                        <TableCell>{formatDate(pln.A_AdjDate)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">No insurance records found for RX #{selectedRx}</Typography>
            )
          )}

          {/* Tab 4: Details Summary */}
          {dialogTab === TAB_DETAILS && (
            detailLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : detailError ? (
              renderDetailError()
            ) : rxDetail && rxDetail.rxDetails.length > 0 ? (
              <Grid container spacing={2}>
                {(() => {
                  // The open fill's own row. GP-1368: a fill opened from Fill
                  // History re-targets this dialog, so falling back to the head
                  // of the series would label another fill's DIN, SIG and
                  // quantities as this Rx's.
                  const rx = rxDetail.rxDetails.find((r: any) => String(r.RxNum) === String(selectedRx));
                  if (!rx) {
                    return (
                      <Grid item xs={12}>
                        <Typography color="text.secondary">
                          No detail available for RX #{selectedRx}
                        </Typography>
                      </Grid>
                    );
                  }
                  return (
                    <>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">Drug Name</Typography>
                        <Typography fontWeight="600">{rx.BrandName || rx.GenericName}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">DIN</Typography>
                        <Typography fontFamily="monospace">{rx.DrgDIN || rx.DIN}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">Doctor</Typography>
                        <Typography>{rx.DocName || '—'}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">Schedule</Typography>
                        <Typography>{rx.Schedule || '—'}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">SIG</Typography>
                        <Typography>{rx.SIG || '—'}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">Auth Qty</Typography>
                        <Typography>{rx.AuthQty}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">Remaining Qty</Typography>
                        <Typography>{rx.RemQty}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">Unlimited Refills</Typography>
                        <Typography>{rx.UnlimitedRefills ? 'Yes' : 'No'}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">Auto Refill</Typography>
                        <Typography>{rx.AutoRefill ? 'Yes' : 'No'}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">Follow-up Date</Typography>
                        <Typography>{formatDate(rx.FollowUpDate)}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">Rx Expiry</Typography>
                        <Typography>{formatDate(rx.RxExpiryDate)}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">Written Date</Typography>
                        <Typography>{formatDate(rx.WrittenDate)}</Typography>
                      </Grid>
                    </>
                  );
                })()}
              </Grid>
            ) : (
              <Typography color="text.secondary">No detail available</Typography>
            )
          )}

          {/* Tab 5: Notes for this RX */}
          {dialogTab === TAB_NOTES && selectedRx && (
            <Box sx={{ minHeight: 300 }}>
              <PatientNotesPanel
                patientId={patientId}
                defaultTab="all"
                defaultTags={[`rx:${selectedRx}`]}
                relatedPrescriptionId={String(selectedRx)}
                compact={false}
                showHeader={false}
              />
            </Box>
          )}
        </DialogContent>

        {/* Product verification photo lightbox (PRX-68) — one instance for
            whichever photo the Hardcopy tab's camera badge was clicked on. */}
        <VerificationPhotoLightbox
          open={openVerificationPhoto !== null}
          onClose={() => setOpenVerificationPhoto(null)}
          pharmacyId={pharmacyId}
          orderId={openVerificationPhoto?.orderId || ''}
          photo={openVerificationPhoto}
          orderNumber={openVerificationPhoto?.orderNumber}
        />
      </Dialog>

      {/* GP-1358: the full counseling session, opened from the Rx Detail dialog's
          Counseling tab — the same dialog RX History opens. */}
      {counselingRecordToView && (
        <CounselingDialog
          open
          onClose={() => {
            setCounselingRecordToView(null);
            refreshCounseling();
          }}
          orderData={{
            pharmacyId: pharmacyId || '',
            orderId: counselingRecordToView.orderId || undefined,
            rxNumber: counselingRecordToView.rxNumber || String(selectedRx ?? ''),
            patientId,
            patientName: counselingRecordToView.patientName,
            drugBrand: counselingRecordToView.drugName,
            din: counselingRecordToView.din,
            doctorName: counselingRecordToView.doctorName,
          }}
          existingRecordId={counselingRecordToView.id}
          existingChecklist={counselingRecordToView.checklist as any}
          existingStatus={counselingRecordToView.status}
          existingCounselingBy={counselingRecordToView.counselingBy}
          existingMethod={counselingRecordToView.method}
          existingComments={counselingRecordToView.comments}
          existingDeclinedReason={counselingRecordToView.declinedReason}
          existingAgentName={counselingRecordToView.agentName}
          existingAgentRelationship={counselingRecordToView.agentRelationship}
          existingDateCounselled={counselingRecordToView.dateCounselled}
          onComplete={() => {
            setCounselingRecordToView(null);
            refreshCounseling();
          }}
        />
      )}
    </Box>
  );
};

export default AllPrescriptions;
